import { useMutation, useQueryClient } from "@tanstack/react-query";
import { configuration } from "../../../../configuration";
import { FetchError, GetMeetingNotesDto } from "../../../types";
import { useAccessToken } from "../../../use-access-token";
import { queryKeys } from "../../queryKeys";

type Variables = {
  meetingGuid: string;
  meetingNoteGuid: string;
};

const useDeleteMeetingNoteRequest = () => {
  const { getAccessToken } = useAccessToken();

  async function deleteMeetingNoteRequest(variables: Variables): Promise<void> {
    const accessToken = await getAccessToken();

    const response = await fetch(`${configuration.apiRootUrl}/meeting-notes/${variables.meetingNoteGuid}`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      method: "delete",
    });

    if (!response.ok) {
      throw new FetchError(response);
    }
  }

  return { deleteMeetingNoteRequest };
};

const useDeleteMeetingNote = () => {
  const { deleteMeetingNoteRequest } = useDeleteMeetingNoteRequest();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteMeetingNoteRequest,
    onMutate: async (variables) => {
      await queryClient.cancelQueries(queryKeys.meetings.getMeetingNotes(variables.meetingGuid));
      const cachedData = queryClient.getQueryData<GetMeetingNotesDto>(
        queryKeys.meetings.getMeetingNotes(variables.meetingGuid)
      );

      if (cachedData) {
        const optimisticallyUpdatedData: GetMeetingNotesDto = {
          ...cachedData,
          meetingNotes: cachedData.meetingNotes.filter((meetingNote) => meetingNote.guid !== variables.meetingNoteGuid),
        };

        queryClient.setQueryData(queryKeys.meetings.getMeetingNotes(variables.meetingGuid), optimisticallyUpdatedData);
      }

      return { cachedData };
    },
    onError: (_error, variables, context) => {
      queryClient.setQueryData(queryKeys.meetings.getMeetingNotes(variables.meetingGuid), context?.cachedData);
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries(queryKeys.meetings.getMeetingNotes(variables.meetingGuid));
    },
  });
};

export { useDeleteMeetingNote };
